import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AssetService } from '../../../shared/asset.service';
import { EnvironmentService } from '../../environment/environment.service';
import { Header } from '@aptsi-types';

export interface MapperDialogData {
  type: 'asset' | 'environment';
  alreadySelectedIds: string[];
}

@Component({
  selector: 'app-zone-mapper-dialog',
  templateUrl: './zone-mapper-dialog.component.html',
  styleUrls: ['./zone-mapper-dialog.component.scss']
})
export class ZoneMapperDialogComponent {
  // Pagination
  itemsPerPage = 7;
  currentPage = 1;
  totalPages = 0;
  totalItems = 0;

  // Search
  searchText = '';
  filterByHeader = 'all';
  isSearch = false;

  // Data
  dataRows: any[] = [];
  selectedRows: any[] = []; // maintain selected items across pages

  // Table headers (based on actual API fields)
  headers: Header[] = [];
  buttonConfig = {};

  isLoading = false;

  constructor(
    public dialogRef: MatDialogRef<ZoneMapperDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MapperDialogData,
    private assetService: AssetService,
    private environmentService: EnvironmentService
  ) {
    this.initHeaders();
    this.fetchData();
  }

  private initHeaders(): void {
    if (this.data.type === 'asset') {
      this.headers = [
        { name: 'Asset ID', property: 'assetId', showInDropdown: true, description: '' },
        { name: 'Asset Name', property: 'name', showInDropdown: true, description: '' }
      ];
    } else {
      this.headers = [
        { name: 'Environment ID', property: 'id', showInDropdown: true, description: '' },
        { name: 'Environment Name', property: 'environmentName', showInDropdown: true, description: '' }
      ];
    }
  }

  async fetchData(): Promise<void> {
    this.isLoading = true;
    try {
      let response: any;
      const pageIndex = this.currentPage - 1;

      if (this.data.type === 'asset') {
        if (this.isSearch) {
          response = await this.assetService
            .getAssetsSearch(pageIndex, this.searchText, this.filterByHeader)
            .toPromise();
        } else {
          response = await this.assetService.getAssets(pageIndex).toPromise();
        }
      } else {
        if (this.isSearch) {
          response = await this.environmentService
            .getEnvironmentsSearch(pageIndex, this.searchText, this.filterByHeader)
            .toPromise();
        } else {
          response = await this.environmentService.getEnvironments(pageIndex).toPromise();
        }
      }

      // Normalize response: extract data rows and pagination metadata
      let rawRows: any[] = [];
      let totalPagesFromResponse = 0;
      let currentPageFromResponse = 0;

      if (response && response['Resource retrieved successfully']) {
        rawRows = response['Resource retrieved successfully'];
        // Metadata might be at root or inside first item
        totalPagesFromResponse = response.totalPages ?? (rawRows[0]?.totalPages ?? 0);
        currentPageFromResponse = response.currentPage ?? (rawRows[0]?.currentPage ?? 0);
      } else if (Array.isArray(response)) {
        rawRows = response;
        totalPagesFromResponse = response[0]?.totalPages ?? 0;
        currentPageFromResponse = response[0]?.currentPage ?? 0;
      } else {
        rawRows = [];
      }

      // Transform rows to have a unified 'id' field and keep original fields
      this.dataRows = rawRows.map((item: any) => {
        if (this.data.type === 'asset') {
          return {
            ...item,
            id: item.assetId   // map assetId to id for consistent selection
          };
        } else {
          return {
            ...item,
            id: item.id        // environment already has id
          };
        }
      });

      // Set pagination
      if (totalPagesFromResponse > 0) {
        this.totalPages = totalPagesFromResponse;
        this.currentPage = currentPageFromResponse + 1; // assuming API returns 0-indexed
      } else {
        // fallback: compute from data length
        this.totalPages = Math.ceil(this.dataRows.length / this.itemsPerPage);
      }

      // Pre-select rows that are already mapped
      this.preselectRows();

    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private preselectRows(): void {
  const preselected = this.dataRows.filter(row =>
    this.data.alreadySelectedIds.includes(row.id)
  );

  const otherPageSelections = this.selectedRows.filter(
    row => !this.dataRows.some(d => d.id === row.id)
  );

  this.selectedRows = [...otherPageSelections, ...preselected];
}


get selectedRowKeys(): string[] {
  return this.selectedRows.map(row => row.id);
}

  // Called when table selection changes
onSelectionChanged(selected: any[]): void {

  const currentPageIds = this.dataRows.map(r => r.id);

  this.selectedRows = this.selectedRows.filter(
    r => !currentPageIds.includes(r.id)
  );

  this.selectedRows = [
    ...this.selectedRows,
    ...selected
  ];
}

  search(): void {
    this.isSearch = !!this.searchText;
    this.currentPage = 1;
    this.fetchData();
  }

  resetSearch(): void {
    this.searchText = '';
    this.filterByHeader = 'all';
    this.isSearch = false;
    this.currentPage = 1;
    this.fetchData();
  }

  changePage(page: number): void {
    this.currentPage = page;
    this.fetchData();
  }

  confirm(): void {
    // Return selected rows as they are (each has 'id' and appropriate name field)
    this.dialogRef.close(this.selectedRows);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  get totalPagesValue(): number {
    return this.totalPages;
  }
}